/**
 * Account state for the profile page.
 *
 * Kept apart from `useAuth`, which answers only "who is signed in" and is read by the whole
 * shell. This is the fuller picture — sign-in methods, sessions, the GitHub summary — and it
 * is fetched when someone opens the page rather than on every render of the application.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

import { profileApi } from '@/lib/api/profile'
import { queryKeys } from '@/lib/queryClient'

export function useProfile() {
  return useQuery({ queryKey: queryKeys.profile, queryFn: profileApi.get, staleTime: 30_000 })
}

export function useSessions() {
  return useQuery({
    queryKey: queryKeys.profileSessions,
    queryFn: profileApi.sessions,
    // Short, because this list is how someone checks that a device they just signed out is
    // really gone. A stale "still signed in" here is the one answer that must not persist.
    staleTime: 5_000,
  })
}

/** Invalidate everything a change to the account can move. */
function useRefreshAccount() {
  const queryClient = useQueryClient()
  return async () => {
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: queryKeys.profile }),
      queryClient.invalidateQueries({ queryKey: queryKeys.profileSessions }),
      // The shell's account menu reads the display name from the session query.
      queryClient.invalidateQueries({ queryKey: queryKeys.session }),
    ])
  }
}

export function useUpdateDisplayName() {
  const queryClient = useQueryClient()
  const refresh = useRefreshAccount()
  return useMutation({
    mutationFn: (displayName: string) => profileApi.updateDisplayName(displayName),
    onSuccess: async (updated) => {
      // The endpoint returns the whole profile as the server now holds it, so adopt that
      // rather than waiting for a refetch: the page shows what was actually saved, not what
      // was typed, and the two cannot disagree for a second in between.
      queryClient.setQueryData(queryKeys.profile, updated)
      await refresh()
    },
  })
}

export function useChangePassword() {
  const refresh = useRefreshAccount()
  return useMutation({
    mutationFn: (body: { current_password?: string | null; new_password: string }) =>
      profileApi.changePassword(body),
    onSuccess: refresh,
  })
}

export function useRevokeSession() {
  const refresh = useRefreshAccount()
  return useMutation({
    mutationFn: (sessionId: number) => profileApi.revokeSession(sessionId),
    onSuccess: refresh,
  })
}

export function useRevokeOtherSessions() {
  const refresh = useRefreshAccount()
  return useMutation({ mutationFn: profileApi.revokeOtherSessions, onSuccess: refresh })
}
